import * as THREE from "three";

/**
 * Couche "villes" : quelles etiquettes afficher, et a quelle taille.
 *
 * CE QUI A CHANGE. Le pipeline coupait autrefois aux 15 communes les plus
 * peuplees et gravait un `tier` (taille/couleur du marqueur) dans le fichier.
 * Deux problemes : le choix editorial etait fige au moment de la generation,
 * et le classement par population brute remonte des communes de banlieue —
 * sur l'Ardeche, quatre des quinze etiquettes appartenaient a l'agglomeration
 * de Valence ou d'Annonay, pendant que Privas, la prefecture, heritait du
 * marqueur le plus discret.
 *
 * Le datapack livre maintenant largement (CITY_COUNT = 60) et c'est LA VUE
 * qui tranche, via trois reglages complementaires (cf. layerSpecs.js) :
 *
 *   - `min_population` : le seuil evident, qui repond a "montre-moi seulement
 *     les vraies villes" ;
 *   - `min_spacing_km` : le remede a l'effet banlieue, qu'aucun seuil ne
 *     traite — Guilherand-Granges est peuplee, elle est juste au mauvais
 *     endroit ;
 *   - `hidden` : la sortie de secours, parce qu'aucune heuristique ne
 *     couvrira le cas ou l'auteur veut juste retirer une ville qui gene son
 *     propos.
 */

// Trois paliers de marqueur, attribues par RANG DANS L'ENSEMBLE AFFICHE et
// non dans le fichier : filtrer a cinq villes doit laisser une hierarchie
// lisible, pas cinq marqueurs identiques.
export const CITY_TIERS = {
  capital: { color: "#db6c52", ring: 1.6, beam: 1.7 },
  subcapital: { color: "#54a8ba", ring: 1.25, beam: 1.35 },
  town: { color: "#fdedc4", ring: 1, beam: 1 },
};

// Proportions plutot que rangs fixes : le premier dixieme des etiquettes
// affichees prend le marqueur majeur, le tiers suivant le marqueur moyen. Sur
// 5 villes ca donne 1 / 1 / 3, sur 40 ca donne 4 / 12 / 24 — dans les deux cas
// une hierarchie, jamais un aplat.
const TIER_BREAKS = [
  { share: 0.1, tier: "capital" },
  { share: 0.4, tier: "subcapital" },
];

function tierForRank(displayRank, displayedCount) {
  // Position calculee sur le rang ZERO-INDEXE : la premiere ville tombe donc
  // toujours a 0 et prend le marqueur majeur, quel que soit l'effectif.
  // Diviser le rang 1-indexe donnerait 1/7 = 0,14 sur sept villes, au-dessus
  // du premier palier — et une carte ou aucune ville ne domine.
  const position = (displayRank - 1) / Math.max(displayedCount, 1);
  for (const { share, tier } of TIER_BREAKS) {
    if (position <= share) return tier;
  }
  return "town";
}

/**
 * Villes retenues pour une vue, dans l'ordre decroissant de population, avec
 * leur `tier` recalcule.
 *
 * L'ecartement se fait en espace MONDE et non en espace ecran : un declutter
 * ecran, recalcule a chaque frame, ferait clignoter les etiquettes des qu'on
 * orbite — inacceptable sur une carte projetee que l'animateur fait tourner
 * en parlant. En espace monde, la selection est stable quel que soit l'angle.
 *
 * L'algorithme est glouton par population decroissante : une ville n'est
 * retenue que si aucune ville DEJA retenue (donc plus peuplee) ne se trouve
 * dans son rayon. C'est ce qui fait qu'une banlieue disparait au profit de sa
 * ville-centre, et jamais l'inverse.
 */
export function selectCities(cities, settings = {}) {
  const {
    min_population: minPopulation = 0,
    min_spacing_km: minSpacingKm = 0,
    max_count: maxCount = Infinity,
    hidden = [],
  } = settings;

  const hiddenSet = new Set(hidden || []);
  const spacingM = Math.max(minSpacingKm || 0, 0) * 1000;
  const spacingSq = spacingM * spacingM;

  const candidates = (cities || [])
    .filter((city) => !hiddenSet.has(city.code_insee))
    .filter((city) => (city.population || 0) >= (minPopulation || 0))
    .slice()
    .sort((a, b) => (b.population || 0) - (a.population || 0));

  const kept = [];
  for (const city of candidates) {
    if (kept.length >= maxCount) break;
    if (spacingSq > 0) {
      const tooClose = kept.some((other) => {
        const dx = city.x - other.x;
        const dy = city.y - other.y;
        return dx * dx + dy * dy < spacingSq;
      });
      if (tooClose) continue;
    }
    kept.push(city);
  }

  return kept.map((city, index) => ({
    ...city,
    tier: tierForRank(index + 1, kept.length),
  }));
}

/**
 * Bornes utiles aux curseurs du composer, tirees des donnees reelles plutot
 * que d'un ordre de grandeur devine : un departement rural et une metropole
 * n'ont pas le meme plafond de population.
 */
export function cityCatalog(cities) {
  const list = cities || [];
  return {
    count: list.length,
    populationMax: list.reduce((max, city) => Math.max(max, city.population || 0), 0),
    list: list.map((city) => ({
      id: city.code_insee,
      label: `${city.name} (${(city.population || 0).toLocaleString("fr-FR")} hab.)`,
    })),
  };
}

/**
 * Classement de repli pour les datapacks anterieurs, qui portaient un `tier`
 * ecrit par le pipeline ou un simple libelle administratif. Sans lui, un
 * territoire genere avant cette refonte perdrait toute hierarchie visuelle.
 */
export function legacyTier(city) {
  if (city?.tier && CITY_TIERS[city.tier]) return city.tier;
  const statut = (city?.statut || "").toLowerCase();
  if (statut.includes("préfecture") && !statut.includes("sous")) return "capital";
  if (statut.includes("sous-préfecture")) return "subcapital";
  return "town";
}

// --- Marqueurs 3D ---------------------------------------------------------

function roundRectPath(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/**
 * ETIQUETTE A TAILLE D'ECRAN CONSTANTE.
 *
 * Les noms de villes etaient floues, et pour deux raisons qui s'additionnent.
 * L'etiquette etait un sprite en unites MONDE (~6 km de large) : vue de loin,
 * quand le departement tient dans l'ecran, elle faisait 60 px de large et son
 * texte 6 px de haut — illisible, quelle que soit la definition. Et sa texture
 * de 512 px etait alors reduite de huit fois par les mipmaps, ce qui rend un
 * texte en bouillie meme quand il est assez grand.
 *
 * Elle est donc maintenant dessinee EN PIXELS D'ECRAN, comme le fait toute
 * carte : `sizeAttenuation: false` lui donne la meme taille quel que soit le
 * zoom, et le canvas est trace a la definition exacte qu'elle occupera a
 * l'ecran (hauteur de la vue × ratio de pixels), sans mipmaps — un texel pour
 * un pixel, le seul cas ou un texte de canvas est net. Le decluttering, lui,
 * reste en espace monde (cf. selectCities) : la selection ne bouge pas quand
 * on orbite, et les auteurs affichent de huit a quinze villes par etape.
 */
const LABEL_HEIGHT_SHARE = 0.034; // hauteur de la boite : 37 px sur un 1080p

function createLabelSprite(text, accentColor, screen) {
  const boxH = Math.max(24, Math.round(LABEL_HEIGHT_SHARE * screen.viewportHeight * screen.pixelRatio));
  const fontPx = Math.round(boxH * 0.6);
  const padX = Math.round(boxH * 0.5);
  const border = Math.max(1.5, boxH * 0.06);
  // Le trait est centre sur le contour : sans marge il serait coupe en deux.
  const margin = Math.ceil(border);
  // 500 : la Condensed n'existe qu'en Medium (500) et UltraBold (800). Un 700
  // demandé retombait sur l'UltraBold, trop lourde pour un nom de ville.
  const font = `500 ${fontPx}px 'GTWalsheimPro-Condensed', sans-serif`;

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  ctx.font = font;
  const textWidth = Math.ceil(ctx.measureText(text).width);
  canvas.width = textWidth + padX * 2 + margin * 2;
  canvas.height = boxH + margin * 2;
  // Redimensionner un canvas remet son contexte a zero, police comprise.
  ctx.font = font;

  roundRectPath(ctx, margin, margin, canvas.width - margin * 2, boxH, boxH * 0.28);
  ctx.fillStyle = "rgba(10,26,36,0.78)";
  ctx.fill();
  ctx.lineWidth = border;
  ctx.strokeStyle = accentColor;
  ctx.stroke();
  ctx.fillStyle = "#f4f8f9";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, canvas.width / 2, margin + boxH / 2);

  const tex = new THREE.CanvasTexture(canvas);
  tex.generateMipmaps = false;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  const mat = new THREE.SpriteMaterial({
    map: tex, transparent: true, depthWrite: false, sizeAttenuation: false,
  });
  const sprite = new THREE.Sprite(mat);
  // Ancree par le bas : c'est le pied de l'etiquette qui se pose sur le
  // sommet du faisceau, et il y reste a tout niveau de zoom.
  sprite.center.set(0.5, 0);
  // Sans attenuation, l'echelle d'un sprite se lit en hauteur de vue : une
  // hauteur de 2·tan(fov/2) couvre exactement l'ecran. Ni la largeur de la
  // vue ni la distance n'entrent en jeu.
  const viewHeight = 2 * Math.tan(THREE.MathUtils.degToRad(screen.fov) / 2);
  const height = (canvas.height / (screen.viewportHeight * screen.pixelRatio)) * viewHeight;
  sprite.scale.set(height * (canvas.width / canvas.height), height, 1);
  // Encombrement a l'ecran, en pixels CSS : c'est ce que l'occultation
  // compare (cf. occludeCityLabels), et il ne depend pas du zoom.
  return { sprite, width: canvas.width / screen.pixelRatio, height: canvas.height / screen.pixelRatio };
}

/**
 * OCCULTATION DES ETIQUETTES EN ESPACE ECRAN.
 *
 * A taille d'ecran constante, deux villes voisines finissent par se recouvrir
 * quand on s'eloigne — c'est la contrepartie de rester lisible. Une carte
 * regle ca en ne dessinant pas l'etiquette qui en recouvrirait une plus
 * importante A CE ZOOM : la ville reste sur la carte (halo et faisceau), seul
 * son nom s'efface, et il revient des qu'on se rapproche.
 *
 * Ce n'est PAS le declutter en espace ecran que selectCities refuse : la
 * selection des villes, elle, ne bouge pas. Ici on ne fait que masquer, dans
 * l'ordre de priorite deja etabli (les marqueurs sont ranges par population
 * decroissante), et avec une HYSTERESIS : une etiquette cachee ne se
 * redessine que lorsqu'elle a retrouve une marge franche. Sans elle, une
 * orbite lente ferait clignoter les etiquettes a la limite du recouvrement.
 *
 * Quinze etiquettes au plus, un produit en carre par image : negligeable.
 */
const SHOW_AGAIN_MARGIN = 8; // px CSS de degagement exige pour reafficher
const projected = new THREE.Vector3();

export function occludeCityLabels(markers, camera, width, height) {
  const kept = [];
  markers.forEach((marker) => {
    const { label, labelSize } = marker;
    if (!labelSize) return;
    label.getWorldPosition(projected).project(camera);
    // Derriere la camera, ou hors champ : rien a comparer, rien a montrer.
    if (projected.z > 1 || Math.abs(projected.x) > 1.2 || Math.abs(projected.y) > 1.2) {
      label.visible = false;
      return;
    }
    const cx = ((projected.x + 1) / 2) * width;
    const bottom = ((1 - projected.y) / 2) * height;
    const rect = {
      left: cx - labelSize.width / 2, right: cx + labelSize.width / 2,
      top: bottom - labelSize.height, bottom,
    };
    const margin = label.visible ? 0 : SHOW_AGAIN_MARGIN;
    const covered = kept.some((other) => (
      rect.left < other.right + margin && rect.right > other.left - margin
      && rect.top < other.bottom + margin && rect.bottom > other.top - margin
    ));
    label.visible = !covered;
    if (!covered) kept.push(rect);
  });
}

/**
 * Marqueur d'une ville : halo au sol + faisceau vertical + etiquette.
 *
 * Les trois pieces sont dimensionnees par le `tier`, qui depend du filtrage :
 * les marqueurs sont donc RECONSTRUITS quand la selection change, et non
 * simplement masques. Le cout est contenu par l'appelant, qui ne reconstruit
 * que si la selection a REELLEMENT change (cf. citiesSelectionKey dans
 * TerrainViewer) — glisser un curseur d'espacement de 0 a 10 km ne produit
 * qu'une poignee de selections distinctes, pas une par pixel parcouru.
 *
 * `screen` ({ fov, viewportHeight, pixelRatio }) dit a quelle definition
 * dessiner l'etiquette : elle est tracee pour l'ecran d'aujourd'hui, et
 * l'appelant reconstruit les marqueurs si cet ecran change vraiment.
 */
export function createCityMarker(city, { sampleAt, worldDiag, zRange, exaggeration, screen }) {
  const sample = sampleAt(city.x, city.y);
  if (!sample) return null;
  const style = CITY_TIERS[city.tier] || CITY_TIERS[legacyTier(city)];

  const group = new THREE.Group();
  group.position.set(sample.x, sample.rawH * exaggeration, sample.z);

  const ringRadius = Math.max(worldDiag * 0.0055, 350) * style.ring;
  const ringGeo = new THREE.RingGeometry(ringRadius * 0.72, ringRadius, 40);
  ringGeo.rotateX(-Math.PI / 2);
  const ring = new THREE.Mesh(
    ringGeo,
    new THREE.MeshBasicMaterial({
      color: style.color, transparent: true, opacity: 0.85,
      side: THREE.DoubleSide, depthWrite: false,
    })
  );
  ring.position.y = 5;
  group.add(ring);

  const beamHeight = zRange * 0.55 * style.beam;
  const beamGeo = new THREE.CylinderGeometry(ringRadius * 0.1, ringRadius * 0.22, beamHeight, 14, 1, true);
  const beam = new THREE.Mesh(
    beamGeo,
    new THREE.MeshBasicMaterial({
      color: style.color, transparent: true, opacity: 0.3,
      side: THREE.DoubleSide, depthWrite: false, blending: THREE.AdditiveBlending,
    })
  );
  beam.position.y = beamHeight / 2;
  group.add(beam);

  const { sprite: label, width, height } = createLabelSprite(city.name, style.color, screen);
  label.position.y = beamHeight + ringRadius * 0.3;
  group.add(label);

  const hitGeo = new THREE.CylinderGeometry(ringRadius * 1.5, ringRadius * 1.5, beamHeight + ringRadius, 10);
  const hitMesh = new THREE.Mesh(hitGeo, new THREE.MeshBasicMaterial({ visible: false }));
  hitMesh.position.y = beamHeight / 2;
  group.add(hitMesh);

  return {
    group, ring, beam, label, labelSize: { width, height }, hitMesh,
    rawH: sample.rawH, data: city,
  };
}

export function disposeCityMarker(marker) {
  marker.ring.geometry.dispose();
  marker.ring.material.dispose();
  marker.beam.geometry.dispose();
  marker.beam.material.dispose();
  marker.hitMesh.geometry.dispose();
  marker.hitMesh.material.dispose();
  marker.label.material.map?.dispose();
  marker.label.material.dispose();
}
